import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link as LinkIcon, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { BrainSyncOrbit } from './OnboardingOverlay';
import StarterVideos from './StarterVideos';

/**
 * Empty-state welcome panel shown the first time a user lands on their
 * dashboard with zero bookmarks. Provides two paths of least
 * resistance: (1) inline URL quick-add that opens the Add modal
 * pre-filled, (2) a full-width row of curated pre-processed starter
 * videos (see <StarterVideos/>) that the user can watch inline before
 * one-click saving to their library.
 */

// Pull just the first name for the greeting - full names like
// "Jane Doe-Smith" feel formal/marketing-y inside the app shell.
const firstNameOf = (name) => {
    if (!name || typeof name !== 'string') return null;
    const trimmed = name.trim();
    if (!trimmed) return null;
    return trimmed.split(/\s+/)[0];
};

export default function FirstRunWelcome({ onQuickAdd, onOpenAdd, userName, onStarterSaved }) {
    const [url, setUrl] = useState('');
    const firstName = firstNameOf(userName);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!url.trim()) {
            onOpenAdd?.();
            return;
        }
        // URL input → open the Add modal pre-filled so the user can
        // pick categories / add notes before submit.
        onQuickAdd?.(url.trim());
    };

    return (
        <motion.div
            className="max-w-5xl mx-auto py-8 sm:py-12 px-4"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            data-testid="first-run-welcome"
        >
            <div className="grid lg:grid-cols-[1fr,360px] gap-8 lg:gap-12 items-center">
                <div>
                    {firstName && (
                        <motion.p
                            className="text-base sm:text-lg font-medium text-foreground/80 mb-3"
                            initial={{ opacity: 0, x: -6 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.45, delay: 0.05 }}
                            data-testid="first-run-greeting"
                        >
                            Welcome, <span className="text-primary font-semibold">{firstName}</span>.
                        </motion.p>
                    )}

                    <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-3 tracking-tight" style={{ lineHeight: '1.2' }}>
                        Drop any link here.<br className="hidden sm:inline" />{' '}
                        <span className="text-muted-foreground">We&rsquo;ll turn it into an AI-powered bookmark.</span>
                    </h2>
                    <p className="text-sm text-muted-foreground mb-7 max-w-lg">
                        Videos get transcribed. Articles get summarized. Recipes get structured.
                    </p>

                    {/* Inline URL quick-add */}
                    <form onSubmit={handleSubmit} className="mb-2">
                        <div className="flex items-stretch gap-2 rounded-2xl border border-border bg-white p-2 shadow-sm hover:shadow-md transition-shadow focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10">
                            <div className="flex items-center px-2 text-muted-foreground">
                                <LinkIcon className="w-4 h-4" />
                            </div>
                            <Input
                                type="url"
                                value={url}
                                onChange={(e) => setUrl(e.target.value)}
                                placeholder="Paste a URL - YouTube, TikTok, articles, recipes…"
                                className="flex-1 border-0 shadow-none text-sm focus-visible:ring-0 bg-transparent"
                                autoFocus
                                data-testid="first-run-url-input"
                            />
                            <Button
                                type="submit"
                                size="sm"
                                className="rounded-xl bg-primary text-white hover:bg-primary/90 px-4 whitespace-nowrap"
                                data-testid="first-run-submit"
                            >
                                Save
                                <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Hero - reuses the animated BrainSyncOrbit from the
                    onboarding overlay. Only shown on lg+ so mobile
                    keeps vertical real-estate for the URL input. */}
                <div className="hidden lg:flex items-center justify-center">
                    <motion.div
                        className="relative w-full aspect-square rounded-3xl overflow-hidden flex items-center justify-center bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-950 shadow-[0_20px_60px_-20px_rgba(6,78,59,0.55)] ring-1 ring-emerald-800/40"
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="first-run-hero-orbit"
                    >
                        <div
                            aria-hidden
                            className="absolute inset-0 opacity-80"
                            style={{
                                background:
                                    'radial-gradient(circle at 50% 45%, rgba(16,185,129,0.22), transparent 60%)',
                            }}
                        />
                        <div className="scale-[1.55] origin-center">
                            <BrainSyncOrbit />
                        </div>
                    </motion.div>
                </div>
            </div>

            {/* Full-width row of pre-processed starter videos. Each
                card embeds the real YouTube player so the user can
                watch before deciding to save. */}
            <StarterVideos onSaved={onStarterSaved} />
        </motion.div>
    );
}
